import { 
  faListCheck, 
  faUsersViewfinder, 
  faStar, 
  faChartLine, 
  faCheckCircle, 
  faFlag, 
  faBullseye, 
  faUserClock, 
  faCrown, 
  faMedal, 
  faUserCheck,
  faUsers
} from "@fortawesome/free-solid-svg-icons";

export const dayOnesRoleDescription = {
  roleId: 'dayOnes',
  title: 'Day Ones',
  sections: [
    // Overview
    {
      id: 'overview',
      title: 'Overview',
      displayTitle: 'Day Ones',
      subtitle: 'Participate. Influence. Build',
      description: 'An exclusive early-access role on Spitface Battles beta, with official recognition reserved for top participants. Engagement in battles, judging, audience scoring, and community discussions powers the beta forward. If you\'re joining the Day Ones, be ready to engage on every front from day one.',
      contentItems: [
        '**Beta Window:** 12 months or until V1 launch',
        '**Signup Capacity:** 500'
      ],
      highlights: [
        '250 Official Spots',
        'High Commitment',
        'Performance-Based'
      ],
      moreItems: [
        { key: "viewing", label: "Viewing", icon: faUsersViewfinder },
        { key: "judging", label: "Judging", icon: faListCheck },
        { key: "community", label: "Engagement", icon: faUsers },
        { key: "feedback", label: "Feedback", icon: faStar }
      ]
    },

    // Why This Role Matters
    {
      id: 'whyThisRoleMatters',
      title: 'Why This Role Matters',
      displayTitle: '',
      subtitle: 'Impact. Direction. Legacy.',
      description: 'Day Ones drive early momentum on Spitface Battles and keep systems sharp. Your engagement shapes the platform, sustains beta activity, and lays the groundwork for others to build on. What happens here carries forward beyond beta and becomes part of the foundation others build on.',
      cards: [
        {
          id: 'influenceImpact',
          title: 'Influence & Impact',
          icon: faFlag,
          items: [
            'Influence platform direction.',
            'Drive community development.'
          ]
        },
        {
          id: 'shapePlatform',
          title: 'Shape Platform',
          icon: faBullseye,
          items: [
            'Sustain competitive momentum.',
            'Refine scoring and other systems.'
          ]
        },
        {
          id: 'prestigeLegacy',
          title: 'Prestige & Legacy',
          icon: faStar,
          items: [
            'Earn permanent recognition.',
            'Your presence becomes SFB history.'
          ]
        }
      ]
    },

    // Who Should Apply
    {
      id: 'whoShouldApply',
      title: 'Who Should Apply',
      displayTitle: '',
      subtitle: 'Ready. Reliable. Engaged.',
      description: 'This role is for participants who drive the beta forward through consistent engagement. Come prepared, contribute steadily, and help shape the platform experience for others. Your involvement sets the pace and strengthens the foundation of Spitface Battles.',
      lists: [
        {
          title: 'Ideal For:',
          items: [
            'Already active in battle rap',
            'Able to commit to ongoing participation',
            'Seeking a structured, high-impact role'
          ]
        },
        {
          title: 'Not Ideal For:',
          items: [
            'Low-commitment or occasional participation',
            'Inconsistent availability or difficulty meeting recurring expectations'
          ]
        }
      ]
    },

    {
      "id": "whatYoullDo",
      "title": "What You'll Do",
      "displayTitle": "",
      "subtitle": "Watch. Judge. Stay Active.",
      "description": "This role requires active participation across all core areas of the platform. You will watch battles, participate in judging, contribute audience scorecards, and engage in community discussions. Each area requires follow-through and consistent involvement over time. Step in, stay committed, and help shape the beta experience from day one.",
      "tiles": [
        {
          "id": "viewing",
          "icon": faUsersViewfinder,
          "title": "Viewing",
          "label": "Watch battles throughout beta"
        }, {
          id: 'judging',
          icon: faListCheck,
          title: 'Judging',
          label: 'Judge battles and submit scorecards on time'

        }, {
          id: 'audience',
          icon: faUserCheck,
          title: 'Audience Scoring',
          label: 'Add scorecards outside of official judging'
        },  {
          id: 'engagement',
          icon: faUsers,
          title: 'Engagement',
          label: 'Participate in discussions and provide feedback'
        }
      ]
    },

    // What You'll Get
    {
      "id": "whatYoullGet",
      "title": "What You'll Get",
      "displayTitle": "",
      "subtitle": "Status. Recognition. Influence.",
      description: 'Day One members receive exclusive status, permanent recognition, and early access to platform features. Each tier provides defined benefits, from premium features to Hall of Fame placement and special badges. These rewards highlight your position as one of the first to shape the Spitface Battles experience.',
      contentItems: ['**Important:** Full beta participation earns top consideration for official status, subject to role limits.'],
      "tiles": [
        {
          "id": "official",
          "icon": faCrown,
          "title": "Day Ones",
          label: 'Top 250 provisional members unlock lifetime premium access, Hall of Fame placement, and a permanent Day One badge and battle marker.'
        },
        {
          "id": "runnerUp",
          "icon": faMedal,
          "title": "Runner-Up Status",
          label: 'Up to 4 years of premium access, Hall of Fame placement, and priority consideration for community leadership.'
        },
        {
          "id": "provisional",
          "icon": faUserClock,
          "title": "Provisional Status",
          label: 'Gain gradual premium access, a temporary Day One battle marker, and influence on the platform during beta.'
        }
      ]
    },

    // Requirements
    {
      id: 'requirements',
      title: 'Requirements',
      displayTitle: '',
      subtitle: 'Watch. Contribute. Perform.',
      description: 'Your participation drives the platform forward. Contributions are measurable, meaningful, and consistent, reflecting both engagement and impact. Maintaining steady involvement ensures you stay connected, build reputation, and define your standing among Day Ones.',
      tiles: [
        {
          id: 'standards',
          icon: faChartLine,
          title: 'Performance Standards',
          label: 'Maintain 90% consistency across all metrics. Status is earned through sustained performance.'
        },
        {
          id: 'markers',
          icon: faCheckCircle,
          title: 'Consistency Markers',
          label: 'Complete your viewing, judging, and scoring targets. Stay active in discussions and contribute meaningfully to the community.'
        }
      ],
      contentItems: [
        '**Important:** Metrics and thresholds may be adjusted before closed beta launch.'
      ],
      tables: [
        {
          title: 'Requirements',
          headers: ['', 'phase 1\nclosed beta', 'phase 2\nopen beta', 'phase 3\nopen beta'],
          rows: [
            { 
              requirement: 'viewing', 
              metric: 'verses', 
              timeframe: 'monthly', 
              phase1: '20', 
              phase2: '12', 
              phase3: '20', 
              execution: 'Watch battles to maintain platform activity' 
            },
            { 
              requirement: 'judging', 
              metric: 'scorecards', 
              timeframe: 'monthly', 
              phase1: '4', 
              phase2: '3', 
              phase3: '4', 
              execution: 'Submit accurate, fair scorecards for assigned battles' 
            },
            { 
              requirement: 'audience', 
              metric: 'scorecards', 
              timeframe: 'total', 
              phase1: '30', 
              phase2: '75', 
              phase3: '30', 
              execution: 'Provide additional audience scorecards outside official rounds' 
            },
            { 
              requirement: 'engagement', 
              metric: 'comments', 
              timeframe: 'weekly', 
              phase1: '5', 
              phase2: '5', 
              phase3: '5', 
              execution: 'Contribute comments and insights to support community discussion' 
            }
          ]
        }
      ]
    },

    // Join Now
    {
      "id": "joinNow",
      "title": "Join Now",
      "displayTitle": "",
      "subtitle": "Confirm. Commit. Start Now.",
      "description": "Confirm you understand the Day One role requirements, eligibility, rewards, and community standards before registering.",
      checkboxes: [
        {
          id: 'commitmentPerformanceV1',
          label: 'I understand rewards start at 3 months and status is earned through consistent performance.'
        },
        {
          id: 'trackingAndEligibilityV1',
          label: 'I understand leaderboards track eligibility, contribution, and progression.'
        },
        {
          id: 'communityEngagementV1',
          label: 'I will stay active, contribute to discussions, and participate in Discord during beta.'
        },
        {
          id: 'requirementsAndStatusV1',
          label: 'I understand requirements may evolve and final status depends on performance across all metrics.'
        }
      ]
    }
  ]
};